import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CHART, money } from "@/lib/api";
import { Card, Overline, KpiCard } from "@/components/common";
import { toast } from "sonner";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Sparkle, ArrowRight, Check } from "lucide-react";

const axis = { stroke: "#A1A1AA", fontSize: 11, tickLine: false, axisLine: false };
const tip = { contentStyle: { borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12, fontFamily: "Manrope" } };

export default function FnB() {
  const [d, setD] = useState(null);
  const [done, setDone] = useState({});
  const nav = useNavigate();
  useEffect(() => { api.get("/fnb").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="h-96 animate-pulse bg-white border border-[#E5E7EB] rounded-xl" />;

  const maxLoc = Math.max(...d.locations.map((l) => l.value));
  const maxProd = Math.max(...d.top_products.map((p) => p.revenue));

  return (
    <div className="space-y-8">
      <div className="relative rounded-xl overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1484156818044-c040038b0719?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-[#16201B]/85" />
        <div className="relative p-8">
          <Overline className="!text-white/50">Food & Beverage</Overline>
          <h1 className="font-display text-3xl font-semibold text-white tracking-tight mt-1">F&B Performance</h1>
          <p className="text-white/60 mt-1">Restaurant, beverage cart, turn stand, and bar.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {d.kpis.map((k) => <KpiCard key={k.label} {...k} testid={`fnb-kpi-${k.label.toLowerCase().replace(/[^a-z]/g, "-")}`} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-6">
          <Overline>Revenue by Location</Overline>
          <div className="mt-4 space-y-4">
            {d.locations.map((l, i) => (
              <div key={l.name}>
                <div className="flex items-center justify-between text-sm mb-1.5"><span className="text-[#1C1F1E] font-medium">{l.name}</span><span className="font-mono2 text-[#52525B]">${l.value.toLocaleString()} · {l.checks} checks</span></div>
                <div className="h-2 rounded-full bg-[#F3F4F6] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(l.value / maxLoc) * 100}%`, background: CHART[i] }} /></div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <Overline>Sales by Hour</Overline>
          <div className="h-48 mt-4"><ResponsiveContainer>
            <AreaChart data={d.by_hour} margin={{ left: -18, right: 8, top: 8 }}>
              <defs><linearGradient id="fnb" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C7A24A" stopOpacity={0.25} /><stop offset="100%" stopColor="#C7A24A" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF1EF" vertical={false} />
              <XAxis dataKey="hour" {...axis} /><YAxis {...axis} tickFormatter={(v) => `$${v}`} />
              <Tooltip {...tip} formatter={(v) => [money(v), "Revenue"]} />
              <Area type="monotone" dataKey="revenue" stroke="#C7A24A" strokeWidth={2} fill="url(#fnb)" />
            </AreaChart>
          </ResponsiveContainer></div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <Overline>Top-Selling Products</Overline>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-4">
            {d.top_products.map((p, i) => (
              <div key={p.name}>
                <div className="flex items-center justify-between text-sm mb-1"><span className="text-[#1C1F1E] font-medium">{p.name}</span><span className="font-mono2 text-[#52525B]">${p.revenue.toLocaleString()} · {p.units}u</span></div>
                <div className="h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden"><div className="h-full rounded-full bg-[#2F7A57]" style={{ width: `${(p.revenue / maxProd) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-4"><Sparkle size={18} className="text-[#C7A24A]" /><h2 className="font-display text-xl font-semibold tracking-tight text-[#1C1F1E]">F&B Opportunities</h2></div>
        <div className="grid md:grid-cols-2 gap-4">
          {d.opportunities.map((o) => (
            <Card key={o.title} className="p-6 border-[#0F3F2D]/12 bg-[#0F3F2D]/[0.02]">
              <Overline className="!text-[#0F3F2D]">{o.title}</Overline>
              <p className="text-sm text-[#1C1F1E] mt-2 leading-relaxed">{o.body}</p>
              <div className="flex items-center justify-between mt-4">
                <span className="font-mono2 font-semibold text-[#0F3F2D]">{o.impact}</span>
                {done[o.title] ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#059669] font-medium"><Check size={15} />Done</span>
                ) : (
                  <button data-testid={`fnb-opp-action`} onClick={() => { setDone((s) => ({ ...s, [o.title]: true })); toast.success(o.action); }} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F3F2D] hover:gap-2 transition-all">{o.action}<ArrowRight size={14} /></button>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
